// ─────────────────────────────────────────────────────────────────────────────
// Synthetic stop sequence and trip roster for the trip-level demos.
//
// ANONYMISATION: stops use ICAO/NATO phonetic callsigns rather than invented
// place names. Invented names that sound convincingly local tend to collide
// with real hamlets, alpages and lieux-dits — the regional naming morphology
// is productive, and micro-toponyms are poorly indexed, so "no search result"
// never proves a name is free. Callsigns are non-existent by construction,
// need no gazetteer check, and stay easy to talk about in a demo
// ("the forced-entry cluster at Foxtrot").
//
// Timings are synthetic too: the original sequence spacing was itself a
// fingerprint of a real service.
// ─────────────────────────────────────────────────────────────────────────────

import { profileFor } from './fleet';
import { doorHealth, DOOR_IDS as UNIT_DOOR_IDS } from './doors';

export type StopRole = 'terminus' | 'interchange' | 'intermediate';

export interface StopDef {
  /** Three-letter code. Used on chart axes — short enough never to truncate. */
  code: string;
  /** Callsign shown in headers, selectors and tooltips. */
  name: string;
  role: StopRole;
  /** Minutes after departure. Actual clock time depends on the trip. */
  offsetMin: number;
  /** 0–1 relative boarding intensity, before the trip's own scaling. */
  busy: number;
}

export const STOP_DEFS: StopDef[] = [
  { code: 'ALF', name: 'Alfa',    role: 'terminus',     offsetMin:  0, busy: 0.90 },
  { code: 'BRV', name: 'Bravo',   role: 'intermediate', offsetMin:  9, busy: 0.50 },
  { code: 'CHA', name: 'Charlie', role: 'interchange',  offsetMin: 21, busy: 0.80 },
  { code: 'DLT', name: 'Delta',   role: 'intermediate', offsetMin: 32, busy: 0.30 },
  { code: 'ECH', name: 'Echo',    role: 'intermediate', offsetMin: 40, busy: 0.40 },
  { code: 'FOX', name: 'Foxtrot', role: 'interchange',  offsetMin: 49, busy: 0.85 },
  { code: 'GLF', name: 'Golf',    role: 'intermediate', offsetMin: 57, busy: 0.70 },
  { code: 'HTL', name: 'Hotel',   role: 'intermediate', offsetMin: 66, busy: 0.35 },
  { code: 'IND', name: 'India',   role: 'intermediate', offsetMin: 73, busy: 0.50 },
  { code: 'JUL', name: 'Juliett', role: 'terminus',     offsetMin: 82, busy: 0.95 },
];

// ── Trips ────────────────────────────────────────────────────────────────────

export interface TripDef {
  /** Service identifier, e.g. 'S1042'. */
  id: string;
  /** Departure clock time from the first stop. */
  departure: string;
  /** Short description of the service period. */
  label: string;
  /** Loading multiplier applied to every stop's base intensity. */
  busyScale: number;
  /** Offsets the deterministic generator so each trip differs. */
  seedOffset: number;
}

export const TRIPS: TripDef[] = [
  { id: 'S1042', departure: '06:42', label: 'Morning peak', busyScale: 1.00, seedOffset: 0 },
  { id: 'S1148', departure: '11:48', label: 'Midday',       busyScale: 0.55, seedOffset: 977 },
  { id: 'S1712', departure: '17:12', label: 'Evening peak', busyScale: 0.95, seedOffset: 1954 },
  { id: 'S2036', departure: '20:36', label: 'Late evening', busyScale: 0.40, seedOffset: 2931 },
];

// ── Door layout ──────────────────────────────────────────────────────────────
// L = left (platform side), R = right (trackside). Wagen A→D, positions 1 and 2.

export const DOOR_IDS: string[] = UNIT_DOOR_IDS;

// ── Generated activity ───────────────────────────────────────────────────────

export interface DoorEvent {
  doorId: string;
  openings: number;
  totalOpenMs: number;
  avgOpenMs: number;
  forcedEvents: number;
  health: number; // 0–100
}

export interface StopData {
  stopId: string;
  code: string;
  stopName: string;
  role: StopRole;
  arrivalTime: string;
  dwellMs: number;
  doors: DoorEvent[];
}

/**
 * Deterministic pseudo-random in [0, 1).
 *
 * NOTE the Math.abs: `(Math.sin(x) * 233280) % 1` alone returns (-1, 1), which
 * caused two bugs — every negative sample passed the `< forcedProb` test
 * regardless of the probability, and Math.ceil of a negative produced zero or
 * negative event counts. Keep this in step with data/doors.ts and
 * data/anomaly.ts, which already clamp the same way.
 */
function seed(n: number) { return Math.abs(Math.sin(n * 9301 + 49297) * 233280) % 1; }

/** Add minutes to a HH:MM string, wrapping at midnight. */
function addMinutes(hhmm: string, minutes: number): string {
  const [h, m] = hhmm.split(':').map(Number);
  const total = (h * 60 + m + minutes) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * Deterministic sample data for one trip. Patterns baked in: platform doors
 * busier than trackside; Charlie and Foxtrot are busy interchanges; door C2L
 * has a developing health issue; B1R has forced history. Those persist across
 * trips — they are properties of the unit, not of the service.
 */
export function getStops(unitId: string, tripId: string): StopData[] {
  const trip = TRIPS.find(t => t.id === tripId) ?? TRIPS[0];
  const p = profileFor(unitId);
  const off = trip.seedOffset + p.seedOffset;

  return STOP_DEFS.map((def, si) => {
    const busy = Math.min(1, def.busy * trip.busyScale * p.loadScale);

    const doors: DoorEvent[] = DOOR_IDS.map((doorId, di) => {
      const isL = doorId.endsWith('L');

      const openings = isL
        ? Math.max(0, Math.round(
            busy
            * (doorId.startsWith('B') || doorId.startsWith('C') ? 1.15 : 0.85)
            * (0.7 + seed(off + si * 100 + di) * 0.6)
            * 8,
          ))
        : 0;

      const avgOpenMs = openings > 0
        ? Math.round(15000 + openings * 3500 + seed(off + si * 50 + di) * 8000)
        : 0;

      // Misuse frequency scales with the unit profile; the count is always at
      // least 1 when an event fires, and a second is possible on abuse-heavy
      // units. Never negative.
      const forcedProb = Math.min(0.6, (openings > 5 ? 0.25 : openings > 2 ? 0.1 : 0.02) * p.misuseScale);
      const fires = isL && openings > 0 && seed(off + si * 200 + di * 3) < forcedProb;
      const forcedEvents = fires
        ? (seed(off + si + di * 7) < 0.25 * p.misuseScale ? 2 : 1)
        : 0;

      // Health is a unit-level property, so it ignores the trip offset.
      const health = Math.max(40, doorHealth(unitId, doorId) - forcedEvents * 3);

      return {
        doorId,
        openings,
        totalOpenMs: avgOpenMs * openings,
        avgOpenMs,
        forcedEvents,
        health,
      };
    });

    return {
      stopId: `${trip.id}-${def.code}`,
      code: def.code,
      stopName: def.name,
      role: def.role,
      arrivalTime: addMinutes(trip.departure, def.offsetMin),
      dwellMs: Math.round(25000 + busy * 45000 + seed(off + si) * 15000),
      doors,
    };
  });
}

/** Route caption, e.g. 'Alfa → Juliett · 10 stops'. */
export const ROUTE_CAPTION =
  `${STOP_DEFS[0].name} → ${STOP_DEFS[STOP_DEFS.length - 1].name} · ${STOP_DEFS.length} stops`;
